import { useCallback, useEffect, useState } from 'react';
import { totalScore } from '../game/gameState';
import { getGameMap } from '../game/maps';
import { formatScore, ROUNDS_PER_GAME } from '../game/scoring';
import { useCountdown } from '../hooks/useCountdown';
import type { useGame } from '../hooks/useGame';
import { fallbackProviders, getProvider } from '../providers';
import Attribution from './Attribution';
import GuessPanel from './GuessPanel';
import { IconAlert, IconClock, IconClose, IconGlobe, IconHome, IconRefresh } from './Icons';
import PanoramaView from './panorama/PanoramaView';
import RoundResult from './RoundResult';

type Game = ReturnType<typeof useGame>;

function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function GameScreen({ game }: { game: Game }) {
  const { state, dispatch } = game;
  const map = getGameMap(state.settings.mapId);
  const round = state.rounds[state.roundIndex];
  const roundProvider = round ? getProvider(round.panorama.provider) : getProvider(state.providerId);
  const [viewerReady, setViewerReady] = useState(false);
  const [confirmQuit, setConfirmQuit] = useState(false);
  // The current round scores 0 until it is validated.
  const total = totalScore(state.rounds);

  useEffect(() => setViewerReady(false), [round?.panorama]);

  const submit = useCallback(() => dispatch({ type: 'submit' }), [dispatch]);
  const remaining = useCountdown(
    state.settings.timeLimit,
    state.phase === 'playing' && viewerReady,
    `${state.gameId}-${state.roundIndex}-${round?.panorama.id ?? ''}`,
    () => dispatch({ type: 'submit', timedOut: true }),
  );

  // Enter validates the guess. (Not Space: releasing it would click the focused button of the result screen.)
  useEffect(() => {
    if (state.phase !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (e.target instanceof HTMLButtonElement || e.target instanceof HTMLInputElement || e.target instanceof HTMLAnchorElement) return;
      if (state.pendingGuess) {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.phase, state.pendingGuess, submit]);

  // Notices disappear by themselves.
  useEffect(() => {
    if (!state.notice) return;
    const timer = window.setTimeout(() => dispatch({ type: 'dismiss-notice' }), 7000);
    return () => window.clearTimeout(timer);
  }, [state.notice, dispatch]);

  // Ask before leaving the page in the middle of a game.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const { panoramaDisplayed } = game;
  const onPanoramaReady = useCallback(() => {
    setViewerReady(true);
    panoramaDisplayed();
  }, [panoramaDisplayed]);

  if (state.phase === 'result' && round) {
    return (
      <RoundResult
        round={round}
        roundIndex={state.roundIndex}
        totalScore={total}
        mapId={state.settings.mapId}
        mapKind={roundProvider.mapKind}
        onNext={() => dispatch({ type: 'next' })}
      />
    );
  }

  const alternatives = fallbackProviders(state.providerId, state.settings.mapId);

  return (
    <main className="game-screen">
      {state.phase === 'playing' && round && (
        <PanoramaView
          panorama={round.panorama}
          provider={roundProvider}
          allowMove={state.settings.allowMove}
          onReady={onPanoramaReady}
          onFailed={game.panoramaFailed}
        />
      )}

      <header className="hud">
        <div className="hud__left">
          <span className="brand brand--sm" aria-hidden="true">
            <span className="brand__logo">
              <IconGlobe size={18} />
            </span>
            <span className="brand__name">
              Geo<span>Gessr</span>
            </span>
          </span>
          <button type="button" className="btn btn--glass btn--sm" onClick={() => setConfirmQuit(true)}>
            <IconClose size={16} /> Quitter
          </button>
        </div>
        <div className="hud__stats" aria-live="polite">
          <div className="hud-chip hud-chip--hide-sm">
            <span className="hud-chip__label">Carte</span>
            <span className="hud-chip__value">{map.name}</span>
          </div>
          <div className="hud-chip">
            <span className="hud-chip__label">Manche</span>
            <span className="hud-chip__value">
              {state.roundIndex + 1} / {ROUNDS_PER_GAME}
            </span>
          </div>
          <div className="hud-chip">
            <span className="hud-chip__label">Score</span>
            <span className="hud-chip__value">{formatScore(total)}</span>
          </div>
          {remaining !== null && (
            <div className={`hud-chip hud-chip--timer ${remaining <= 10 ? 'hud-chip--urgent' : ''}`} role="timer">
              <span className="hud-chip__label">
                <IconClock size={12} /> Temps
              </span>
              <span className="hud-chip__value">{formatClock(remaining)}</span>
            </div>
          )}
        </div>
      </header>

      {state.phase === 'playing' && round && (
        <>
          <GuessPanel
            mapKind={roundProvider.mapKind}
            initialView={map.view}
            guess={state.pendingGuess}
            onPick={(guess) => dispatch({ type: 'set-guess', guess })}
            onSubmit={submit}
          />
          <div className="game-attribution">
            <Attribution attribution={round.panorama.attribution} />
          </div>
        </>
      )}

      {state.phase === 'loading' && (
        <div className="overlay" role="status" aria-live="polite">
          <div className="loading-card">
            <div className="globe-loader" aria-hidden="true">
              <IconGlobe size={56} />
            </div>
            <h2>Recherche d’un lieu…</h2>
            <p className="muted">
              Manche {state.roundIndex + 1} / {ROUNDS_PER_GAME} · {map.name} · {getProvider(state.providerId).name}
            </p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={game.goHome}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {state.phase === 'error' && state.error && (
        <div className="overlay" role="alertdialog" aria-labelledby="error-title">
          <div className="error-card">
            <IconAlert size={40} className="error-card__icon" />
            <h2 id="error-title">Impossible de trouver un lieu</h2>
            <p>{state.error.message}</p>
            <p className="muted">Vérifiez votre connexion, réessayez ou choisissez une autre source d’images.</p>
            <div className="error-card__actions">
              {state.error.retryable && (
                <button type="button" className="btn btn--primary" onClick={game.retry} autoFocus>
                  <IconRefresh size={18} /> Réessayer
                </button>
              )}
              {alternatives.map((p) => (
                <button key={p.id} type="button" className="btn btn--ghost" onClick={() => game.switchTo(p.id)}>
                  Utiliser {p.name}
                </button>
              ))}
              <button type="button" className="btn btn--ghost" onClick={game.goHome}>
                <IconHome size={18} /> Menu principal
              </button>
            </div>
          </div>
        </div>
      )}

      {state.notice && (
        <div className="toast" role="status">
          <IconAlert size={18} />
          <span>{state.notice}</span>
          <button type="button" className="icon-btn icon-btn--small" onClick={() => dispatch({ type: 'dismiss-notice' })} aria-label="Fermer le message">
            <IconClose size={16} />
          </button>
        </div>
      )}

      {confirmQuit && (
        <div className="overlay" role="alertdialog" aria-labelledby="quit-title">
          <div className="confirm-card">
            <h2 id="quit-title">Quitter la partie ?</h2>
            <p className="muted">La partie en cours ne sera pas enregistrée.</p>
            <div className="confirm-card__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmQuit(false)} autoFocus>
                Continuer à jouer
              </button>
              <button type="button" className="btn btn--danger" onClick={game.goHome}>
                Quitter
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
